import React, { useState } from "react";
import { apiClient } from "../../lib/api";
import { toast } from "sonner";
import { MessageSquareText, Loader2 } from "lucide-react";

const C = { forest: "#2B4C3B", ink: "#0F172A", dim: "#475569", line: "#E2E0D8" };

// kind: "tp_app" (pending TP application) | "tp" (approved provider) | "driver"
export default function RequestDocsButton({ kind, recordId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [openReq, setOpenReq] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setOpen(true);
    try {
      const { data } = await apiClient.get(`/doc-requests/missing/${kind}/${recordId}`);
      setItems(data.items);
      setOpenReq(data.open_request);
      setPicked(new Set(data.items.filter((i) => !i.present).map((i) => i.key)));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load documents"); setOpen(false); }
  };

  const toggle = (k) => setPicked((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/doc-requests", { kind, record_id: recordId, items: [...picked] });
      toast.success(`Upload link sent to ${[data.sent_to.phone, data.sent_to.email].filter(Boolean).join(" and ")}`);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative inline-block">
      <button type="button" onClick={load} data-testid="request-docs-btn"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border whitespace-nowrap" title="Text + email the applicant an upload link for missing documents" style={{ borderColor: C.forest, color: C.forest }}>
        <MessageSquareText size={14} /> Request docs
      </button>
      {open && (
        <div className="absolute z-50 bottom-full mb-2 left-0 w-80 rounded-xl border bg-white p-4 shadow-xl space-y-3" style={{ borderColor: C.line }} data-testid="request-docs-popover">
          <div className="text-sm font-bold" style={{ color: C.ink }}>Text + email an upload link for:</div>
          {openReq && <div className="text-xs" style={{ color: C.dim }}>A link is already open from {(openReq.created_at || "").slice(0, 10)} — sending replaces it.</div>}
          <div className="space-y-1.5">
            {items.map((i) => (
              <label key={i.key} className="flex items-center gap-2 text-sm" style={{ color: C.ink }}>
                <input type="checkbox" checked={picked.has(i.key)} onChange={() => toggle(i.key)} data-testid={`request-docs-item-${i.key}`} />
                <span>{i.label}</span>
                {i.present && <span className="text-[10px] font-semibold" style={{ color: C.forest }}>on file</span>}
              </label>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 rounded-md border text-xs" style={{ borderColor: C.line }} data-testid="request-docs-cancel-btn">Cancel</button>
            <button type="button" onClick={send} disabled={busy || picked.size === 0} data-testid="request-docs-send-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold text-white disabled:opacity-50" style={{ background: C.forest }}>
              {busy && <Loader2 size={12} className="animate-spin" />} Send request
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
